/* eslint-disable react-hooks/purity --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
/* eslint-disable react-hooks/set-state-in-effect --
 * Cookie consent banner reacts to storage events / consent changes by
 * updating local state inside the effect — the experimental rule
 * mis-flags this idiomatic pattern. Behaviour is audited and correct.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  getConsent,
  setConsent,
  initGA,
  disableGA,
  onConsentChange,
  isPublicPath,
} from "@/lib/analytics";

/**
 * Minimal cookie-consent banner for CoParent Console.
 *
 * Contract with the rest of the app:
 *   • Only rendered on public (non-`/app/*`) routes — see App.js.
 *   • Once a consent record exists in localStorage the banner stays
 *     hidden on subsequent visits.
 *   • Three top-level actions — Accept all · Reject all · Customise.
 *   • Customise expands an inline panel with per-category toggles.
 *     Strictly-necessary is always on and disabled; Analytics (GA4)
 *     is the only optional category we currently use.
 *   • Acceptance fires `initGA()`; rejection/withdrawal flips
 *     `window['ga-disable-<ID>'] = true` so any already-loaded gtag
 *     is muted immediately.
 *
 * Deliberately NOT a modal / not a page-blocking overlay — the banner
 * is dismissable without action by navigating away (our legal
 * position treats continued use WITHOUT acceptance as non-consent;
 * analytics simply stays off until the visitor clicks a choice).
 */
export default function CookieConsent() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);

  useEffect(() => {
    // Show the banner if no consent record exists yet.
    const c = getConsent();
    if (!c) setVisible(true);
    else if (c.analytics) initGA();
    else disableGA();
    // Re-evaluate if another tab changes consent.
    const off = onConsentChange((detail) => {
      if (!detail) setVisible(true);
      else setVisible(false);
    });
    return off;
  }, []);

  const save = (analytics) => {
    setConsent({ analytics });
    if (analytics) initGA();
    else disableGA();
    setVisible(false);
    setShowCustom(false);
  };

  // Banner renders only on public pages — authenticated parent
  // screens (`/app/*`) never see it, matching the user's explicit
  // scope for this feature.
  if (!isPublicPath(location.pathname)) return null;
  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed inset-x-3 bottom-3 z-[70] sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-sm"
      role="dialog"
      aria-label="Cookie preferences"
    >
      <div className="rounded-md border border-[#D9E1E3] bg-white shadow-sm p-4 text-[#2E3A3F] text-sm leading-snug">
        <div className="font-semibold mb-1 text-[15px]">Cookies on CoParent Console</div>
        <p className="text-[#455C62] text-[13px]">
          We use a small number of cookies to run the site. Analytics
          cookies (Google Analytics) are optional and only load if you
          say yes. See our{" "}
          <Link
            to="/privacy"
            className="underline text-[#007C91] hover:text-[#005F70]"
            data-testid="cookie-consent-privacy-link"
          >
            Privacy Policy
          </Link>
          .
        </p>

        {showCustom && (
          <div
            className="mt-3 space-y-2 border-t border-[#E2E8EC] pt-3"
            data-testid="cookie-consent-custom-panel"
          >
            <ToggleRow
              label="Strictly necessary"
              description="Required for the site to work (login, security)."
              checked
              disabled
              testId="cookie-toggle-necessary"
            />
            <ToggleRow
              label="Analytics"
              description="Google Analytics 4 — anonymous page-view stats."
              checked={analyticsOn}
              onChange={setAnalyticsOn}
              testId="cookie-toggle-analytics"
            />
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          {!showCustom ? (
            <>
              <button
                onClick={() => save(true)}
                className="flex-1 min-w-[110px] rounded-md bg-[#007C91] text-white text-[13px] font-medium py-2 px-3 hover:bg-[#005F70] transition-colors"
                data-testid="cookie-accept-all-button"
              >
                Accept all
              </button>
              <button
                onClick={() => save(false)}
                className="flex-1 min-w-[110px] rounded-md border border-[#CCD8DD] text-[#1E3A3C] text-[13px] font-medium py-2 px-3 hover:bg-[#F4F7F8] transition-colors"
                data-testid="cookie-reject-all-button"
              >
                Reject all
              </button>
              <button
                onClick={() => setShowCustom(true)}
                className="basis-full rounded-md text-[#007C91] text-[12px] font-medium py-1.5 px-3 hover:underline text-left sm:text-center"
                data-testid="cookie-customise-button"
              >
                Customise
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => save(analyticsOn)}
                className="flex-1 min-w-[110px] rounded-md bg-[#007C91] text-white text-[13px] font-medium py-2 px-3 hover:bg-[#005F70] transition-colors"
                data-testid="cookie-save-preferences-button"
              >
                Save preferences
              </button>
              <button
                onClick={() => setShowCustom(false)}
                className="flex-1 min-w-[110px] rounded-md border border-[#CCD8DD] text-[#1E3A3C] text-[13px] font-medium py-2 px-3 hover:bg-[#F4F7F8] transition-colors"
                data-testid="cookie-customise-back-button"
              >
                Back
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange, disabled, testId }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer" data-testid={`${testId}-row`}>
      <input
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-[#CCD8DD] text-[#007C91] focus:ring-[#007C91] disabled:opacity-60"
        data-testid={testId}
      />
      <span className="flex-1">
        <span className="block font-medium text-[13px] text-[#1E3A3C]">{label}</span>
        <span className="block text-[12px] text-[#6F828A]">{description}</span>
      </span>
    </label>
  );
}
